/**
 * Shot Analyzer — pose-based shot mechanics analysis (Phase 1).
 *
 * Flow:
 *   1. First-load → mandatory manual modal (acknowledged in localStorage).
 *   2. Camera permission + live video stream from the device.
 *   3. Ghost Silhouette overlay; we compute a `match` score every frame.
 *   4. Once match ≥ 80% for ~0.8s, fade out the silhouette and start a
 *      ~3s pose-sampling window.
 *   5. Compute biomechanics (`analyzeShot`) entirely in-browser.
 *   6. Persist the attempt summary (no video) and ask Claude for the
 *      coaching report. Show the result. Done.
 *
 * No video is ever uploaded — only metrics + the release-frame
 * keypoints, per the privacy decision in iter-77.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Camera, BookOpen, Loader2, Sparkles, RotateCcw,
  CheckCircle2, AlertTriangle, Lock, Download, Mail, Mic, Inbox, Play,
  Target, Upload, Users,
} from 'lucide-react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import ShotAnalyzerManual, { SHOT_MANUAL_ACK_KEY } from '@/components/ShotAnalyzerManual';
import ShotAnalyzerReport from '@/components/ShotAnalyzerReport';
import VoiceRecorderModal from '@/components/VoiceRecorderModal';
import { useLang } from '@/context/LangContext';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { usePoseDetector } from '@/hooks/usePoseDetector';
import ShotHeatMap from '@/components/ShotHeatMap';
import { captureAndUpload, hasConsent, setConsent } from '@/lib/yoloCapture';
import {
  analyzeShot, silhouetteMatch, REFERENCE_SET_SHOT, L,
} from '@/lib/shotBiomechanics';
import { BallTracker } from '@/lib/ballTracker';
import { computeBallMetrics, ballErrors } from '@/lib/ballMetrics';

const MATCH_THRESHOLD = 80;        // % to start countdown
const MATCH_HOLD_MS = 800;         // must be held this long
const CAPTURE_WINDOW_MS = 3000;    // sample window after match

// Skeleton pairs to render the ghost silhouette as connected lines.
const SKELETON_EDGES = [
  [L.L_SHOULDER, L.R_SHOULDER],
  [L.L_SHOULDER, L.L_ELBOW], [L.L_ELBOW, L.L_WRIST],
  [L.R_SHOULDER, L.R_ELBOW], [L.R_ELBOW, L.R_WRIST],
  [L.L_SHOULDER, L.L_HIP], [L.R_SHOULDER, L.R_HIP],
  [L.L_HIP, L.R_HIP],
  [L.L_HIP, L.L_KNEE], [L.L_KNEE, L.L_ANKLE],
  [L.R_HIP, L.R_KNEE], [L.R_KNEE, L.R_ANKLE],
];

function Silhouette({ visible, matched }) {
  // Stronger gold (#FFD700) with a soft dark outline that survives both
  // bright outdoor video and dark indoor video. Stroke widths in
  // *viewBox* units (0..1) — ~0.018 matches ~3px on a 1280px capture.
  const stroke = matched ? '#00FF88' : '#FFD700';
  const fill = matched ? 'rgba(0,255,136,0.18)' : 'rgba(255,215,0,0.18)';
  const dotFill = matched ? '#00FF88' : '#FFD700';
  return (
    <svg
      data-testid="sa-silhouette"
      viewBox="0 0 1 1"
      className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-500 ${visible ? 'opacity-90' : 'opacity-0'}`}
      style={{ filter: matched ? 'drop-shadow(0 0 12px rgba(0,255,136,0.55))' : 'drop-shadow(0 0 6px rgba(0,0,0,0.55))' }}
      preserveAspectRatio="none"
    >
      {/* Head */}
      <ellipse cx="0.5" cy="0.2" rx="0.05" ry="0.07" fill={fill} stroke={stroke} strokeWidth="0.006" />
      {/* Skeleton — render an outline pass + a coloured pass for double-stroke effect */}
      {SKELETON_EDGES.map(([a, b], i) => (
        <line
          key={`o-${i}`}
          x1={REFERENCE_SET_SHOT[a].x} y1={REFERENCE_SET_SHOT[a].y}
          x2={REFERENCE_SET_SHOT[b].x} y2={REFERENCE_SET_SHOT[b].y}
          stroke="rgba(0,15,32,0.7)"
          strokeWidth="0.024"
          strokeLinecap="round"
        />
      ))}
      {SKELETON_EDGES.map(([a, b], i) => (
        <line
          key={`c-${i}`}
          x1={REFERENCE_SET_SHOT[a].x} y1={REFERENCE_SET_SHOT[a].y}
          x2={REFERENCE_SET_SHOT[b].x} y2={REFERENCE_SET_SHOT[b].y}
          stroke={stroke}
          strokeWidth="0.016"
          strokeLinecap="round"
        />
      ))}
      {REFERENCE_SET_SHOT.slice(11).map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="0.014" fill={dotFill} stroke="rgba(0,15,32,0.65)" strokeWidth="0.004" />
      ))}
    </svg>
  );
}

function LiveSkeleton({ landmarks, color = '#10b981', strokeMul = 1, dashed = false }) {
  if (!landmarks) return null;
  return (
    <svg
      data-testid="sa-live-skeleton"
      viewBox="0 0 1 1"
      className="absolute inset-0 w-full h-full pointer-events-none"
      preserveAspectRatio="none"
    >
      {SKELETON_EDGES.map(([a, b], i) => {
        const A = landmarks[a]; const B = landmarks[b];
        if (!A || !B) return null;
        return (
          <line
            key={i}
            x1={A.x} y1={A.y} x2={B.x} y2={B.y}
            stroke={color}
            strokeWidth={0.008 * strokeMul}
            strokeLinecap="round"
            strokeDasharray={dashed ? '0.012,0.012' : undefined}
          />
        );
      })}
    </svg>
  );
}

// Palette used to distinguish the non-shooter skeletons when several
// players are in frame. Muted-cool tones so the shooter (gold) pops.
const PLAYER_COLORS = ['#38bdf8', '#f472b6', '#a78bfa', '#f59e0b', '#4ade80'];

/**
 * Given an array of pose landmarks and a target 2D point, return the
 * index of the pose whose *shooting-hand wrist* is closest to the point.
 * The shooting hand is picked as the wrist that's higher on screen
 * (smaller y) — the one likely holding the ball at release.
 */
function nearestPoseIdxToPoint(poses, pt) {
  if (!poses?.length || !pt) return -1;
  let best = -1; let bestD = Infinity;
  poses.forEach((p, i) => {
    if (!p) return;
    const lw = p[15]; // L_WRIST
    const rw = p[16]; // R_WRIST
    const w = (lw && rw) ? (lw.y < rw.y ? lw : rw) : (lw || rw);
    if (!w) return;
    const d = Math.hypot(w.x - pt.x, w.y - pt.y);
    if (d < bestD) { bestD = d; best = i; }
  });
  return best;
}

/**
 * Rough torso center used as a click-target for manual shooter select.
 * Averages shoulders + hips; falls back to nose if lower body is off-frame.
 */
function poseCenter(p) {
  const idx = [11, 12, 23, 24];
  const pts = idx.map((i) => p?.[i]).filter((x) => x && (x.visibility ?? 1) > 0.35);
  if (!pts.length) return p?.[0] || null;
  const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
  return { x: cx, y: cy };
}

function MetricRow({ labelKey, value, hint, t }) {
  return (
    <div className="flex items-center justify-between text-xs py-1.5 border-b border-white/5 last:border-0">
      <div className="text-slate-300">{t(labelKey)}</div>
      <div className="font-mono tabular-nums text-white">
        {value ?? '—'}
        {hint && <span className="text-slate-500 ml-1">{hint}</span>}
      </div>
    </div>
  );
}

export default function ShotAnalyzer() {
  const { t } = useLang();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const framesRef = useRef([]);
  const timestampsRef = useRef([]);
  const matchSinceRef = useRef(0);
  const captureUntilRef = useRef(0);
  // Phase 2: ball tracker state.
  const ballTrackerRef = useRef(null);
  const ballSamplesRef = useRef([]);
  const videoBoxRef = useRef(null);

  const [showManual, setShowManual] = useState(false);
  // One-time YOLO consent banner. Auto-hides after dismiss; users can
  // toggle in `/profile`.
  const [showYoloBanner, setShowYoloBanner] = useState(
    typeof localStorage !== 'undefined' && localStorage.getItem('pm_yolo_banner_dismissed') !== '1'
  );
  const [stage, setStage] = useState('idle'); // idle | aligning | capturing | analyzing | done | gated
  const [matchPct, setMatchPct] = useState(0);
  const [liveLandmarks, setLiveLandmarks] = useState(null);
  // Multi-pose: keep the *list* of detected skeletons per frame so we can
  // render every player and let the user pick who to analyze.
  const [livePoses, setLivePoses] = useState([]);
  // Which pose (index in livePoses) is the currently-highlighted shooter.
  // Kept in state so the gold highlight is stable across renders.
  const [primaryPoseIdx, setPrimaryPoseIdx] = useState(0);
  // Which detected pose is the "shooter". `null` = automatic (pick the
  // one nearest the ball at release, or best silhouette match otherwise).
  const [selectedShooterIdx, setSelectedShooterIdx] = useState(null);
  // Source mode: live webcam or an uploaded video file.
  const [sourceMode, setSourceMode] = useState('live'); // 'live' | 'video'
  const videoFileRef = useRef(null);
  const fileInputRef = useRef(null);
  const [result, setResult] = useState(null);     // analyzeShot output
  const [ballResult, setBallResult] = useState(null); // computeBallMetrics output
  const [showTrajectory, setShowTrajectory] = useState(true);
  const [rimPoint, setRimPoint] = useState(null);  // {x,y} normalized or null
  const [rimAutoDetecting, setRimAutoDetecting] = useState(false);
  const [aiReport, setAiReport] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);
  const [history, setHistory] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [savedAttemptId, setSavedAttemptId] = useState(null);
  const reportRef = useRef(null);
  // Coach inbox state — attempts shared by other players with the current coach.
  const [inbox, setInbox] = useState([]);
  const [activeInboxAttempt, setActiveInboxAttempt] = useState(null);

  // Multi-pose detector — up to 4 skeletons per frame.
  const pose = usePoseDetector({ numPoses: 4 });
  // Per-frame samples of *all* poses (indexed the same way per frame) so
  // we can pick the shooter after the fact.
  const framesAllRef = useRef([]);

  // Demo mode (`?demo=1`) injects a fake result so the user can preview
  // the PDF report without doing a real analysis. Useful for QA and for
  // the empty-state "what does the report look like?" CTA.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') !== '1') return;
    setResult({
      ok: true,
      side: 'right',
      overall_score: 76,
      release_keypoints: null,
      metrics: {
        elbow_angle_release: 92.4,
        knee_bend_min: 138.2,
        shoulder_alignment: 81,
        balance_score: 74,
        follow_through_ms: 420,
        rhythm_score: 68,
        release_height_norm: 0.68,
        dip_to_release_ms: 380,
        leg_drive_score: 72,
      },
      errors: ['low_release', 'broken_rhythm'],
    });
    setAiReport('Tu codo está perfectamente colocado a 92° en el release y tu balance es sólido (74/100). El problema principal es el set point bajo: estás soltando la pelota a la altura del pecho en vez de por encima de la frente, lo que reduce el arco. Hacé 3×10 form shots frente al espejo enfocándote en subir el punto de liberación a la frente, con el codo extendido completamente. Mantené el follow-through 2 segundos en cada repetición para fijar la memoria muscular.');
    setHistory([
      { id: 'd1', overall_score: 68, created_at: new Date(Date.now() - 7*86400000).toISOString() },
      { id: 'd2', overall_score: 72, created_at: new Date(Date.now() - 5*86400000).toISOString() },
      { id: 'd3', overall_score: 70, created_at: new Date(Date.now() - 3*86400000).toISOString() },
      { id: 'd4', overall_score: 74, created_at: new Date(Date.now() - 1*86400000).toISOString() },
    ]);
    setSavedAttemptId('demo-attempt');
    setStage('done');
  }, []);

  // Gate check + manual ack on mount.
  useEffect(() => {
    let mounted = true;
    api.get('/payments/me')
      .then(({ data }) => {
        if (!mounted) return;
        setHasAccess(!!data?.features?.shot_analyzer);
        if (data?.features?.shot_analyzer) {
          api.get('/shot-analyzer/attempts?limit=10')
            .then(({ data: d }) => mounted && setHistory(d?.attempts || []))
            .catch(() => {});
          // Coach inbox — silently ignored for non-coach users.
          api.get('/shot-analyzer/inbox?limit=20')
            .then(({ data: d }) => mounted && setInbox(d?.attempts || []))
            .catch(() => {});
        }
      })
      .catch(() => mounted && setHasAccess(false));
    if (!localStorage.getItem(SHOT_MANUAL_ACK_KEY)) {
      setShowManual(true);
    }
    return () => { mounted = false; };
  }, []);

  // ── Camera / uploaded video + main RAF loop for the alignment phase ─
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.src = '';
        videoRef.current.loop = false;
        videoRef.current.muted = true;
        await videoRef.current.play();
        pose.start(videoRef.current);
        setSourceMode('live');
        setStage('aligning');
        framesRef.current = [];
        framesAllRef.current = [];
        timestampsRef.current = [];
        ballSamplesRef.current = [];
        setSelectedShooterIdx(null);
        if (!ballTrackerRef.current) ballTrackerRef.current = new BallTracker();
        matchSinceRef.current = 0;
      }
    } catch (e) {
      toast.error(t('sa_camera_denied'));
    }
  };

  // Uploaded video mode — swap the <video> source to the picked file.
  // Same pose+ball pipeline runs, but the user gets to pick the shooter
  // manually because a game clip has multiple players in frame.
  const startFromFile = async (file) => {
    if (!file) return;
    try {
      // Release the previous URL to avoid a leak.
      if (videoFileRef.current) URL.revokeObjectURL(videoFileRef.current);
      const url = URL.createObjectURL(file);
      videoFileRef.current = url;
      // Stop any active camera stream first.
      streamRef.current?.getTracks().forEach((tk) => tk.stop());
      streamRef.current = null;
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = url;
        videoRef.current.loop = false;
        videoRef.current.muted = true;
        videoRef.current.controls = true;
        await videoRef.current.play();
        pose.start(videoRef.current);
      }
      setSourceMode('video');
      // Video mode skips the silhouette match — user picks the shooter,
      // then hits "Analyze this shot" to capture the surrounding window.
      setStage('video-scrub');
      framesRef.current = [];
      framesAllRef.current = [];
      timestampsRef.current = [];
      ballSamplesRef.current = [];
      setSelectedShooterIdx(null);
      if (!ballTrackerRef.current) ballTrackerRef.current = new BallTracker();
    } catch (e) {
      toast.error(t('sa_video_load_fail') || 'No pudimos abrir el video');
    }
  };

  const stopCamera = () => {
    pose.stop();
    streamRef.current?.getTracks().forEach((tk) => tk.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.src = '';
      videoRef.current.controls = false;
    }
    if (videoFileRef.current) {
      URL.revokeObjectURL(videoFileRef.current);
      videoFileRef.current = null;
    }
  };

  // Main loop driven by RAF — reads the latest landmarks from the hook.
  useEffect(() => {
    if (stage === 'idle' || stage === 'done' || stage === 'gated' || stage === 'analyzing') return;
    let alive = true;
    const tick = () => {
      if (!alive) return;
      const poses = pose.landmarksListRef.current || [];
      setLivePoses(poses);
      // The "primary" landmark set = manually selected pose, else the
      // best silhouette match, else the first detected pose. This is
      // the skeleton we use for silhouette matching + biomechanics.
      let primaryIdx = selectedShooterIdx;
      if (primaryIdx == null || !poses[primaryIdx]) {
        // Pick best silhouette match (highest = "most upright shooter").
        let best = -1; let bestM = -1;
        poses.forEach((p, i) => {
          const m = silhouetteMatch(p, REFERENCE_SET_SHOT);
          if (m > bestM) { bestM = m; best = i; }
        });
        primaryIdx = best >= 0 ? best : 0;
      }
      setPrimaryPoseIdx(primaryIdx);
      const lm = poses[primaryIdx] || null;
      setLiveLandmarks(lm);
      const now = performance.now();
      if (stage === 'aligning') {
        const m = lm ? silhouetteMatch(lm, REFERENCE_SET_SHOT) : 0;
        setMatchPct(Math.round(m));
        if (m >= MATCH_THRESHOLD) {
          if (!matchSinceRef.current) matchSinceRef.current = now;
          if (now - matchSinceRef.current >= MATCH_HOLD_MS) {
            // Lock in: start capture.
            captureUntilRef.current = now + CAPTURE_WINDOW_MS;
            framesRef.current = [];
            framesAllRef.current = [];
            timestampsRef.current = [];
            setStage('capturing');
          }
        } else {
          matchSinceRef.current = 0;
        }
      } else if (stage === 'video-scrub') {
        // In uploaded-video mode we don't wait for a silhouette match —
        // the user drives the timeline. We just keep sampling poses so
        // the overlay stays live while they scrub.
      } else if (stage === 'capturing') {
        if (lm) {
          framesRef.current.push(lm);
          timestampsRef.current.push(now);
          // Snapshot ALL poses this frame so we can pick a different
          // shooter after the fact if the user changes their mind.
          framesAllRef.current.push({ t: now, poses: poses.map((p) => p) });
        }
        // Ball tracking — every frame during the capture window.
        const ball = ballTrackerRef.current?.detect(videoRef.current, now);
        if (ball) ballSamplesRef.current.push(ball);
        if (now >= captureUntilRef.current) {
          setStage('analyzing');
          setTimeout(() => doAnalyze(), 50);
          return; // exit loop
        }
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => { alive = false; };
  }, [stage, selectedShooterIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const doAnalyze = async () => {
    // Multi-pose: pick the "shooter" AFTER the fact. If the user tapped
    // a player, honour that. Otherwise, find the pose whose wrist is
    // closest to the ball at its FIRST tracked position (=release).
    const allSamples = framesAllRef.current;
    let frames = framesRef.current;
    let timestamps = timestampsRef.current;
    const ballSamples = ballSamplesRef.current;

    if (selectedShooterIdx == null && ballSamples.length > 0 && allSamples.length > 0) {
      // Ball's first tracked point ≈ release point.
      const release = ballSamples[0];
      // Find the frame whose timestamp is closest to that ball sample.
      let closestFrame = allSamples[0];
      let bestDt = Math.abs(allSamples[0].t - release.t);
      for (const f of allSamples) {
        const dt = Math.abs(f.t - release.t);
        if (dt < bestDt) { bestDt = dt; closestFrame = f; }
      }
      const shooterIdx = nearestPoseIdxToPoint(closestFrame.poses, { x: release.x, y: release.y });
      if (shooterIdx >= 0) {
        // Rebuild frames + timestamps using the shooter's landmarks.
        frames = [];
        timestamps = [];
        for (const snap of allSamples) {
          const p = snap.poses[shooterIdx];
          if (p) {
            frames.push(p);
            timestamps.push(snap.t);
          }
        }
      }
    } else if (selectedShooterIdx != null && allSamples.length > 0) {
      // Manual shooter selection — use that pose across all frames.
      frames = [];
      timestamps = [];
      for (const snap of allSamples) {
        const p = snap.poses[selectedShooterIdx];
        if (p) {
          frames.push(p);
          timestamps.push(snap.t);
        }
      }
    }

    const out = analyzeShot(frames, timestamps);
    // Phase 2 — compute ball trajectory metrics from the captured samples.
    const ballOut = computeBallMetrics(ballSamples, rimPoint);
    stopCamera();
    if (!out.ok) {
      toast.error(t('sa_not_enough_frames'));
      setStage('idle');
      return;
    }
    // Merge ball metrics + errors into the pose result so they ride the
    // existing persistence / Claude / PDF pipeline.
    if (ballOut.ok) {
      const bErrs = ballErrors(ballOut);
      out.metrics.release_angle_deg = ballOut.release_angle_deg;
      out.metrics.apex_height_norm = ballOut.apex_height_norm;
      out.metrics.arc_depth_norm = ballOut.arc_depth_norm;
      out.metrics.arc_curvature = ballOut.arc_curvature;
      out.metrics.arc_quality_score = ballOut.arc_quality_score;
      out.metrics.flight_time_ms = ballOut.flight_time_ms;
      if (ballOut.distance_to_rim_norm != null) {
        out.metrics.distance_to_rim_norm = ballOut.distance_to_rim_norm;
      }
      if (ballOut.rim_made != null) {
        out.metrics.rim_made = ballOut.rim_made;
      }
      out.errors = Array.from(new Set([...(out.errors || []), ...bErrs]));
    }
    setResult(out);
    setBallResult(ballOut.ok ? ballOut : null);
    setStage('done');
    // Persist + request AI report.
    setSaving(true);
    try {
      const payload = {
        overall_score: out.overall_score,
        metrics: out.metrics,
        errors: out.errors,
        release_keypoints: out.release_keypoints,
        distance_zone: 'unknown',
      };
      if (ballOut.ok && ballOut.trajectory) {
        payload.ball_trajectory = ballOut.trajectory;
        if (rimPoint) payload.rim_point = rimPoint;
      }
      const { data: saved } = await api.post('/shot-analyzer/attempts', payload);
      setHistory((h) => [saved, ...h].slice(0, 10));
      setSavedAttemptId(saved.id);
      // Fire-and-forget YOLO dataset capture (opt-in, default on).
      // Never blocks; failures are swallowed inside the helper.
      if (hasConsent() && videoRef.current && ballSamplesRef.current.length) {
        captureAndUpload({
          videoEl: videoRef.current,
          ballSamples: ballSamplesRef.current,
          landmarksAtRelease: frames[Math.floor(frames.length / 2)] || null,
          rimPoint,
          result: ballOut?.ok ? (ballOut.rim_made ? 'make' : 'miss') : 'unknown',
          shotId: saved.id,
          sessionId: saved.session_id,
          distanceNorm: ballOut?.distance_to_rim_norm ?? null,
          angleDeg: ballOut?.release_angle_deg ?? null,
          trajectory: ballOut?.trajectory || [],
        }).catch(() => {});
      }
      try {
        const { data: rep } = await api.post('/shot-analyzer/report', {
          attempt_id: saved.id,
          lang: (localStorage.getItem('pm_lang') || 'es'),
        });
        setAiReport(rep?.report || '');
      } catch { /* report optional */ }
    } catch (err) {
      if (err?.response?.status === 402) {
        setHasAccess(false);
        setStage('gated');
      } else {
        toast.error(err?.response?.data?.detail || t('sa_save_fail'));
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setResult(null);
    setBallResult(null);
    setAiReport('');
    setSavedAttemptId(null);
    framesRef.current = [];
    framesAllRef.current = [];
    timestampsRef.current = [];
    ballSamplesRef.current = [];
    matchSinceRef.current = 0;
    setMatchPct(0);
    setLivePoses([]);
    setSelectedShooterIdx(null);
    setStage('idle');
  };

  /**
   * Capture the current video frame and ask the backend to detect the
   * rim via OpenCV HoughCircles. Falls back gracefully — the user can
   * still tap the rim manually if detection misses.
   */
  const onAutoDetectRim = async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      toast.error(t('sa_camera_needed') || 'Encendé la cámara primero');
      return;
    }
    setRimAutoDetecting(true);
    try {
      // Snapshot the current frame at native size (cheap, <250KB JPEG).
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.8));
      if (!blob) throw new Error('snapshot failed');
      const form = new FormData();
      form.append('frame', blob, 'frame.jpg');
      const r = await api.post('/shot-analyzer/detect-rim', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data?.ok && r.data?.rim) {
        const { x, y } = r.data.rim;
        setRimPoint({ x: +x.toFixed(3), y: +y.toFixed(3) });
        toast.success(
          (t('sa_rim_auto_ok') || 'Aro detectado') +
          ` (${Math.round((r.data.confidence || 0) * 100)}%)`,
        );
      } else {
        toast.error(t('sa_rim_auto_fail') || 'No pudimos detectar el aro — tocá en pantalla');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('sa_rim_auto_fail') || 'No pudimos detectar el aro'));
    } finally {
      setRimAutoDetecting(false);
    }
  };

  /**
   * Generate the PDF as a jsPDF instance from the off-screen template.
   * Returns the pdf object so callers can either `.save()` it locally
   * or convert to base64 for upload.
   */
  const buildPdf = async () => {
    if (!reportRef.current) throw new Error('Report not mounted');
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ]);
    const node = reportRef.current;
    const originalLeft = node.style.left;
    node.style.left = '0';
    node.style.top = '0';
    node.style.zIndex = '-1';
    node.style.visibility = 'hidden';
    const canvas = await html2canvas(node, {
      backgroundColor: '#0A192F',
      scale: 2,
      useCORS: true,
      logging: false,
      windowWidth: 794,
    });
    node.style.left = originalLeft || '-9999px';
    node.style.zIndex = '';
    node.style.visibility = '';

    const img = canvas.toDataURL('image/jpeg', 0.92);
    const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;
    if (imgH <= pageH) {
      pdf.addImage(img, 'JPEG', 0, 0, imgW, imgH);
    } else {
      let remaining = imgH;
      let offset = 0;
      while (remaining > 0) {
        pdf.addImage(img, 'JPEG', 0, -offset, imgW, imgH);
        remaining -= pageH;
        offset += pageH;
        if (remaining > 0) pdf.addPage();
      }
    }
    return pdf;
  };

  const handleExport = async () => {
    if (!result || exporting) return;
    setExporting(true);
    try {
      const pdf = await buildPdf();
      const dateTag = new Date().toISOString().slice(0, 10);
      pdf.save(`shot-analyzer-${dateTag}.pdf`);
    } catch (e) {
      toast.error(t('sa_export_fail'));
    } finally {
      setExporting(false);
    }
  };

  /**
   * Fetch and play a coach voice comment. We hit the authenticated
   * backend endpoint (raw bytes) and turn the response into a blob URL
   * the browser can stream. Required because <audio src=...> can't send
   * the JWT header.
   */
  const playVoiceComment = async (attemptId) => {
    try {
      const resp = await api.get(`/shot-analyzer/voice-comment/${attemptId}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (err) {
      toast.error(t('sa_voice_play_fail'));
    }
  };

  /**
   * Mail the PDF to the user's coach (or to themselves if they ARE the
   * coach). The backend resolves the recipient based on `user.coach_id`.
   */
  const handleSendToCoach = async () => {
    if (!result || sending || !savedAttemptId) {
      if (!savedAttemptId) toast.error(t('sa_send_wait_save'));
      return;
    }
    setSending(true);
    try {
      const pdf = await buildPdf();
      // jsPDF.output('datauristring') returns "data:application/pdf;base64,XXXX"
      const dataUri = pdf.output('datauristring');
      const base64 = dataUri.split(',', 2)[1] || '';
      const { data } = await api.post('/shot-analyzer/send-to-coach', {
        attempt_id: savedAttemptId,
        pdf_base64: base64,
        lang: localStorage.getItem('pm_lang') || 'es',
      });
      toast.success(t('sa_send_ok').replace('{email}', data?.to || ''));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('sa_send_fail'));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => () => stopCamera(), []); // eslint-disable-line

  const ackManual = () => {
    localStorage.setItem(SHOT_MANUAL_ACK_KEY, '1');
  };

  // ── Gated view ────────────────────────────────────────────────────
  if (hasAccess === false) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <BackToDashboard />
          <div className="rounded-xl border-2 border-gold/30 bg-card-steel p-8 text-center">
            <Lock className="w-10 h-10 text-gold mx-auto mb-4" />
            <h1 className="text-2xl font-h text-white">{t('sa_gated_title')}</h1>
            <p className="text-sm text-slate-300 mt-3 max-w-md mx-auto">{t('sa_gated_body')}</p>
            <a
              data-testid="sa-cta-pricing"
              href="/pricing"
              className="inline-block mt-5 px-5 py-2.5 rounded-md bg-gold text-navy font-bold text-sm uppercase tracking-widest hover:brightness-110"
            >
              {t('sa_gated_cta')}
            </a>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <BackToDashboard />
        <header className="mb-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.32em] text-gold/80">{t('sa_eyebrow')}</div>
            <h1 className="text-3xl sm:text-4xl font-h text-white mt-1">{t('sa_title')}</h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">{t('sa_subtitle')}</p>
          </div>
          <button
            data-testid="sa-open-manual"
            onClick={() => setShowManual(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest border border-gold/40 text-gold hover:bg-gold/10"
          >
            <BookOpen className="w-3.5 h-3.5" /> {t('sa_manual_button')}
          </button>
        </header>

        {/* One-time YOLO consent banner. */}
        {showYoloBanner && (
          <div
            data-testid="yolo-consent-banner"
            className="mb-4 rounded-lg border border-purple-400/40 bg-purple-500/10 px-4 py-3 flex items-center gap-3 flex-wrap"
          >
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-purple-300">IA Dataset</span>
            <span className="text-xs text-slate-200 flex-1">
              Tus tiros ayudan a entrenar nuestro modelo de visión por computadora (anónimos, opt-in). Lo podés desactivar en tu perfil.
            </span>
            <button
              data-testid="yolo-banner-opt-out"
              onClick={() => {
                setConsent(false);
                setShowYoloBanner(false);
                localStorage.setItem('pm_yolo_banner_dismissed', '1');
                toast.success('Desactivado. Lo podés reactivar en tu perfil.');
              }}
              className="text-[11px] font-mono uppercase tracking-widest text-slate-300 underline underline-offset-2 hover:text-white"
            >
              No, gracias
            </button>
            <button
              data-testid="yolo-banner-accept"
              onClick={() => {
                setConsent(true);
                setShowYoloBanner(false);
                localStorage.setItem('pm_yolo_banner_dismissed', '1');
              }}
              className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-md bg-purple-500 text-white hover:bg-purple-600"
            >
              Entendido
            </button>
          </div>
        )}

        {/* Phase 2 toolbar — rim calibration + trajectory toggle. */}
        <div className="mb-3 flex items-center justify-between gap-2 flex-wrap text-xs">
          <div className="flex items-center gap-2">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold/80">
              {t('sa_rim_calibrate')}:
            </div>
            {rimPoint ? (
              <span
                data-testid="sa-rim-status"
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-orange-500/15 border border-orange-400/40 text-orange-200 font-mono uppercase tracking-widest text-[10px]"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400" /> {t('sa_rim_set')}
              </span>
            ) : (
              <span className="text-slate-400 italic text-[11px]" data-testid="sa-rim-empty">
                {t('sa_rim_tap_hint')}
              </span>
            )}
            {rimPoint && (
              <button
                data-testid="sa-rim-reset"
                onClick={() => setRimPoint(null)}
                className="text-[10px] font-mono uppercase tracking-widest text-slate-300 underline underline-offset-2 hover:text-white"
              >
                {t('sa_rim_reset')}
              </button>
            )}
            <button
              data-testid="sa-rim-auto"
              onClick={onAutoDetectRim}
              disabled={rimAutoDetecting || stage === 'capturing' || stage === 'analyzing'}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-orange-400/40 text-orange-300 text-[10px] font-mono uppercase tracking-widest hover:bg-orange-500/10 disabled:opacity-50"
              title={t('sa_rim_auto_hint') || 'Detect the rim automatically with computer vision'}
            >
              <Target className="w-3 h-3" /> {rimAutoDetecting ? '…' : (t('sa_rim_auto') || 'Auto')}
            </button>
          </div>
          {ballResult?.trajectory?.length > 1 && (
            <button
              data-testid="sa-toggle-trajectory"
              onClick={() => setShowTrajectory((v) => !v)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-gold/40 text-gold text-[10px] font-mono uppercase tracking-widest hover:bg-gold/10"
            >
              {showTrajectory ? t('sa_hide_trajectory') : t('sa_show_trajectory')}
            </button>
          )}
        </div>

        {/* Camera stage */}
        <div
          ref={videoBoxRef}
          className="relative rounded-xl border-2 border-gold/30 bg-black overflow-hidden aspect-video"
          onClick={(e) => {
            // ── 1) Multi-pose shooter selection (aligning + video-scrub)
            //     Tapping on a player torso selects them as the shooter.
            //     If the tap doesn't hit any pose, fall through to rim
            //     calibration (below).
            if (livePoses.length > 1 && (stage === 'aligning' || stage === 'video-scrub' || stage === 'idle')) {
              const rect = e.currentTarget.getBoundingClientRect();
              const nx = (e.clientX - rect.left) / rect.width;
              const ny = (e.clientY - rect.top) / rect.height;
              let picked = -1; let bestD = 0.09; // ≈9% of frame = generous
              livePoses.forEach((p, i) => {
                const c = poseCenter(p);
                if (!c) return;
                const d = Math.hypot(c.x - nx, c.y - ny);
                if (d < bestD) { bestD = d; picked = i; }
              });
              if (picked >= 0) {
                setSelectedShooterIdx(picked);
                toast.success(t('sa_shooter_locked') || `Tirador #${picked + 1} seleccionado`);
                return;
              }
            }
            // ── 2) Rim calibration fallback (only when idle/done).
            if (stage !== 'idle' && stage !== 'done') return;
            const rect = e.currentTarget.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const y = (e.clientY - rect.top) / rect.height;
            if (x < 0 || x > 1 || y < 0 || y > 1) return;
            setRimPoint({ x: +x.toFixed(3), y: +y.toFixed(3) });
            toast.success(t('sa_rim_set'));
          }}
          data-testid="sa-video-box"
        >
          <video
            ref={videoRef}
            data-testid="sa-video"
            className={`w-full h-full ${sourceMode === 'video' ? 'object-contain bg-black' : 'object-cover'}`}
            playsInline
            muted
          />
          {/* Overlays */}
          <Silhouette visible={sourceMode === 'live' && (stage === 'aligning' || stage === 'idle')} matched={stage === 'aligning' && matchPct >= MATCH_THRESHOLD} />

          {/* Multi-pose: draw every detected skeleton. The "shooter" pose
              renders on top in gold; the others get muted colours + a
              subtle dashed style so the eye is drawn to the shooter. */}
          {livePoses.map((p, i) => {
            const isShooter = i === primaryPoseIdx;
            return (
              <LiveSkeleton
                key={i}
                landmarks={p}
                color={isShooter ? '#FFD700' : PLAYER_COLORS[i % PLAYER_COLORS.length]}
                strokeMul={isShooter ? 1.4 : 0.9}
                dashed={!isShooter}
              />
            );
          })}

          {/* Player badges — floating labels near each skeleton torso so
              the coach knows which pose to tap when there are 5 players
              in frame. Only rendered when we have >1 pose. */}
          {livePoses.length > 1 && livePoses.map((p, i) => {
            const c = poseCenter(p);
            if (!c) return null;
            const isShooter = i === primaryPoseIdx;
            return (
              <div
                key={`badge-${i}`}
                data-testid={`sa-pose-badge-${i}`}
                className={`absolute pointer-events-none px-1.5 py-0.5 rounded-md text-[9px] font-mono uppercase tracking-widest border ${
                  isShooter
                    ? 'bg-gold text-navy border-gold shadow-[0_0_10px_rgba(255,215,0,0.6)]'
                    : 'bg-black/70 text-white border-white/30'
                }`}
                style={{
                  left: `${c.x * 100}%`,
                  top: `${(c.y - 0.05) * 100}%`,
                  transform: 'translate(-50%, -100%)',
                }}
              >
                {isShooter ? '★ ' : ''}P{i + 1}
              </div>
            );
          })}

          {/* Rim marker — shown when calibrated, doesn't block clicks. */}
          {rimPoint && (
            <div
              data-testid="sa-rim-marker"
              className="absolute pointer-events-none"
              style={{
                left: `${rimPoint.x * 100}%`,
                top: `${rimPoint.y * 100}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div className="w-10 h-10 rounded-full border-2 border-orange-400 shadow-[0_0_18px_rgba(251,146,60,0.7)]" />
              <div className="absolute inset-0 flex items-center justify-center text-orange-300 text-[9px] font-mono uppercase tracking-widest">
                {t('sa_rim_set')}
              </div>
            </div>
          )}

          {/* Ball trajectory overlay — shown post-analysis when toggled. */}
          {showTrajectory && ballResult?.trajectory?.length > 1 && stage === 'done' && (
            <svg
              data-testid="sa-trajectory"
              viewBox="0 0 1 1"
              className="absolute inset-0 w-full h-full pointer-events-none"
              preserveAspectRatio="none"
            >
              {/* Polyline of ball positions */}
              <polyline
                points={ballResult.trajectory.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke="#fb923c"
                strokeWidth="0.008"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.9"
              />
              {/* Release + apex markers */}
              {ballResult.release_point && (
                <circle cx={ballResult.release_point.x} cy={ballResult.release_point.y} r="0.012" fill="#34d399" stroke="#0A192F" strokeWidth="0.003" />
              )}
              {ballResult.apex_point && (
                <circle cx={ballResult.apex_point.x} cy={ballResult.apex_point.y} r="0.012" fill="#fbbf24" stroke="#0A192F" strokeWidth="0.003" />
              )}
            </svg>
          )}

          {/* Status badges — pinned top-right OUT of the silhouette area
              so they never compete with the figure the user is matching. */}
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between pointer-events-none gap-2">
            <div className="px-2.5 py-1 rounded-md bg-black/60 border border-white/15 text-[10px] font-mono uppercase tracking-widest text-white">
              {!pose.ready ? t('sa_status_loading_model')
                : stage === 'idle' ? t('sa_status_idle')
                : stage === 'aligning' ? t('sa_status_align_short')
                : stage === 'video-scrub' ? (t('sa_status_video_scrub') || 'Video · elegí al tirador')
                : stage === 'capturing' ? t('sa_status_capturing')
                : stage === 'analyzing' ? t('sa_status_analyzing')
                : t('sa_status_done')}
            </div>
            {stage === 'aligning' && (
              <div className="rounded-md bg-black/60 border border-gold/40 px-3 py-2 text-right backdrop-blur-sm">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white leading-tight" style={{fontWeight: 700}}>
                  {t('sa_align_headline')}
                </div>
                <div className="text-[11px] font-mono text-gold-light mt-0.5 tabular-nums" style={{color: matchPct >= MATCH_THRESHOLD ? '#00FF88' : '#F8E88B'}}>
                  {t('sa_align_target')}: {MATCH_THRESHOLD}% · {matchPct}%
                </div>
              </div>
            )}
            {livePoses.length > 1 && stage !== 'analyzing' && stage !== 'done' && (
              <div className="rounded-md bg-black/60 border border-gold/40 px-2 py-1 text-[9px] font-mono uppercase tracking-widest text-gold" data-testid="sa-multi-pose-count">
                <Users className="w-3 h-3 inline mr-1" />{livePoses.length} {t('sa_players_detected') || 'jugadores'}
              </div>
            )}
          </div>

          {/* Video-scrub action bar — user hits "Analyze this shot" to
              grab a 3s window around the current playhead. */}
          {stage === 'video-scrub' && (
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2 pointer-events-none">
              <div className="px-2.5 py-1.5 rounded-md bg-black/70 border border-white/15 text-[10px] font-mono uppercase tracking-widest text-white pointer-events-auto">
                {selectedShooterIdx == null
                  ? (t('sa_video_pick_shooter') || 'Tocá al tirador o dejalo en auto (bola)')
                  : `★ P${selectedShooterIdx + 1} · ${t('sa_shooter_ready') || 'listo'}`}
              </div>
              <button
                data-testid="sa-video-analyze"
                onClick={() => {
                  captureUntilRef.current = performance.now() + CAPTURE_WINDOW_MS;
                  framesRef.current = [];
                  framesAllRef.current = [];
                  timestampsRef.current = [];
                  ballSamplesRef.current = [];
                  setStage('capturing');
                }}
                className="pointer-events-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-gold text-navy text-xs font-bold uppercase tracking-widest hover:brightness-110"
              >
                <Sparkles className="w-3.5 h-3.5" /> {t('sa_video_analyze') || 'Analizar tiro'}
              </button>
            </div>
          )}

          {/* Idle CTA — two options: live camera or upload video. */}
          {(stage === 'idle' || stage === 'done') && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 gap-2.5">
              <button
                data-testid="sa-start"
                onClick={async () => { reset(); await startCamera(); }}
                disabled={!pose.ready || pose.error}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-gold text-navy text-sm font-bold uppercase tracking-widest hover:brightness-110 disabled:opacity-60"
              >
                {pose.ready ? <Camera className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                {stage === 'done' ? t('sa_new_attempt') : t('sa_start')}
              </button>
              <button
                data-testid="sa-upload-video"
                onClick={() => { reset(); fileInputRef.current?.click(); }}
                disabled={!pose.ready || pose.error}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border-2 border-gold/50 text-gold text-xs font-mono uppercase tracking-widest hover:bg-gold/10 disabled:opacity-60"
              >
                <Upload className="w-3.5 h-3.5" /> {t('sa_upload_video') || 'Subir video'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                data-testid="sa-video-file"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) startFromFile(f);
                  e.target.value = '';
                }}
                className="hidden"
              />
            </div>
          )}
        </div>

        {pose.error && (
          <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {t('sa_model_error')}: {pose.error}
          </div>
        )}

        {/* Results */}
        {result && (
          <div data-testid="sa-result" className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {!ballResult && stage === 'done' && (
              <div className="lg:col-span-3 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 flex items-center gap-2" data-testid="sa-no-ball">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>{t('sa_no_ball')} — {t('sa_tip_orange')}</span>
              </div>
            )}
            <div className="rounded-xl border border-gold/30 bg-gradient-to-br from-gold/10 to-transparent p-5 lg:col-span-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80">{t('sa_overall_score')}</div>
              <div className="text-6xl font-h text-white tabular-nums mt-1">{result.overall_score}<span className="text-2xl text-slate-400">/100</span></div>
              <div className="mt-3 text-xs text-slate-300">
                <CheckCircle2 className="w-3.5 h-3.5 inline text-emerald-400 mr-1" /> {t('sa_shot_side')}: {result.side === 'right' ? t('sa_side_right') : t('sa_side_left')}
              </div>
              {result.errors.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300 mb-2">{t('sa_errors')}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {result.errors.map((e) => (
                      <span key={e} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-amber-500/10 border border-amber-500/30 text-amber-200">
                        <AlertTriangle className="w-3 h-3" /> {t(`sa_err_${e}`)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <button
                onClick={reset}
                className="mt-5 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-white text-xs font-mono uppercase tracking-widest"
              >
                <RotateCcw className="w-3.5 h-3.5" /> {t('sa_new_attempt')}
              </button>
              <button
                data-testid="sa-download-report"
                onClick={handleExport}
                disabled={exporting || saving}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-gold text-navy text-xs font-mono uppercase tracking-widest font-bold hover:brightness-110 disabled:opacity-60"
              >
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {exporting ? t('sa_exporting') : t('sa_download_report')}
              </button>
              <button
                data-testid="sa-send-to-coach"
                onClick={handleSendToCoach}
                disabled={sending || saving || exporting || !savedAttemptId}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md border-2 border-gold/50 text-gold text-xs font-mono uppercase tracking-widest font-bold hover:bg-gold/10 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!savedAttemptId ? t('sa_send_wait_save') : ''}
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                {sending ? t('sa_sending') : t('sa_send_to_coach')}
              </button>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/30 p-5 lg:col-span-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80 mb-3">{t('sa_metrics')}</div>
              <MetricRow t={t} labelKey="sa_m_elbow"      value={result.metrics.elbow_angle_release}  hint="°" />
              <MetricRow t={t} labelKey="sa_m_knee"       value={result.metrics.knee_bend_min}        hint="°" />
              <MetricRow t={t} labelKey="sa_m_release_h"  value={result.metrics.release_height_norm} />
              <MetricRow t={t} labelKey="sa_m_balance"    value={result.metrics.balance_score}        hint="/100" />
              <MetricRow t={t} labelKey="sa_m_align"      value={result.metrics.shoulder_alignment}   hint="/100" />
              <MetricRow t={t} labelKey="sa_m_rhythm"     value={result.metrics.rhythm_score}         hint="/100" />
              <MetricRow t={t} labelKey="sa_m_leg_drive"  value={result.metrics.leg_drive_score}      hint="/100" />
              <MetricRow t={t} labelKey="sa_m_dip_ms"     value={result.metrics.dip_to_release_ms}    hint="ms" />
              <MetricRow t={t} labelKey="sa_m_follow_ms"  value={result.metrics.follow_through_ms}    hint="ms" />
              {/* Phase 2 — only rendered when the ball tracker succeeded */}
              {result.metrics.release_angle_deg != null && (
                <MetricRow t={t} labelKey="sa_m_release_angle" value={result.metrics.release_angle_deg} hint="°" />
              )}
              {result.metrics.apex_height_norm != null && (
                <MetricRow t={t} labelKey="sa_m_apex"          value={result.metrics.apex_height_norm} />
              )}
              {result.metrics.arc_quality_score != null && (
                <MetricRow t={t} labelKey="sa_m_arc_quality"   value={result.metrics.arc_quality_score} hint="/100" />
              )}
              {result.metrics.flight_time_ms != null && (
                <MetricRow t={t} labelKey="sa_m_flight_time"   value={result.metrics.flight_time_ms}   hint="ms" />
              )}
              {result.metrics.distance_to_rim_norm != null && (
                <MetricRow t={t} labelKey="sa_m_rim_distance"  value={result.metrics.distance_to_rim_norm} />
              )}
              {result.metrics.rim_made != null && (
                <MetricRow t={t} labelKey="sa_m_rim_made"
                  value={result.metrics.rim_made ? t('sa_rim_yes') : t('sa_rim_no')} />
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-black/30 p-5 lg:col-span-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80 mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> {t('sa_ai_report')}
              </div>
              {saving ? (
                <div className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('sa_generating_report')}</div>
              ) : aiReport ? (
                <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{aiReport}</p>
              ) : (
                <div className="text-xs text-slate-400 italic">{t('sa_no_report')}</div>
              )}
            </div>
          </div>
        )}

        {/* Heat map — only shown to users with at least one attempt. */}
        {history.length > 0 && (
          <div className="mt-6">
            <ShotHeatMap days={90} />
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="mt-6 rounded-xl border border-white/10 bg-black/30 p-4">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gold/80 mb-3">{t('sa_history')}</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {history.map((a) => (
                <div key={a.id} className="rounded-md bg-black/40 border border-white/5 p-2 text-center relative">
                  <div className="text-2xl font-h text-white tabular-nums">{Math.round(a.overall_score)}</div>
                  <div className="text-[9px] font-mono text-slate-400 uppercase tracking-widest">{new Date(a.created_at).toLocaleDateString()}</div>
                  {a.coach_comment && (
                    <button
                      data-testid={`sa-history-voice-${a.id}`}
                      onClick={() => playVoiceComment(a.id)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-gold/20 border border-gold flex items-center justify-center hover:bg-gold/40"
                      title={t('sa_voice_from_coach')}
                    >
                      <Mic className="w-2.5 h-2.5 text-gold" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coach Inbox — only renders if the coach has any shared attempts */}
        {inbox.length > 0 && (
          <div data-testid="sa-coach-inbox" className="mt-6 rounded-xl border-2 border-gold/30 bg-gradient-to-br from-gold/5 to-transparent p-4">
            <div className="flex items-center gap-2 mb-3">
              <Inbox className="w-4 h-4 text-gold" />
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gold/80">{t('sa_inbox_title')}</div>
              <span className="text-[10px] text-slate-400 font-mono">({inbox.length})</span>
            </div>
            <div className="space-y-2">
              {inbox.map((a) => (
                <div
                  key={a.id}
                  data-testid={`sa-inbox-row-${a.id}`}
                  className="flex items-center justify-between gap-2 p-2.5 rounded-md bg-black/40 border border-white/5 hover:border-gold/30 transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center text-xs font-bold text-gold shrink-0">
                      {(a.player_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{a.player_name}</div>
                      <div className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">
                        {new Date(a.shared_at || a.created_at).toLocaleString()} · {a.errors?.length || 0} {t('sa_inbox_errors')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="text-2xl font-h text-white tabular-nums">{Math.round(a.overall_score)}</div>
                    {a.coach_comment ? (
                      <button
                        data-testid={`sa-inbox-listen-${a.id}`}
                        onClick={() => playVoiceComment(a.id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-700/70 hover:bg-emerald-600 text-white text-[10px] font-mono uppercase tracking-widest"
                        title={t('sa_voice_listen')}
                      >
                        <Play className="w-3 h-3" /> {t('sa_voice_played')}
                      </button>
                    ) : (
                      <button
                        data-testid={`sa-inbox-reply-${a.id}`}
                        onClick={() => setActiveInboxAttempt(a)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-gold text-navy text-[10px] font-mono uppercase tracking-widest font-bold hover:brightness-110"
                      >
                        <Mic className="w-3 h-3" /> {t('sa_voice_reply')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5 text-[10px] text-slate-500 italic text-center">{t('sa_disclaimer')}</div>
      </div>

      {showManual && (
        <ShotAnalyzerManual
          mandatory={!localStorage.getItem(SHOT_MANUAL_ACK_KEY)}
          onAck={ackManual}
          onClose={() => setShowManual(false)}
        />
      )}

      {/* Off-screen PDF report template — only mounted once we have a result */}
      {result && (
        <ShotAnalyzerReport
          ref={reportRef}
          attempt={{
            ...result,
            created_at: new Date().toISOString(),
            ball_trajectory: ballResult?.trajectory || null,
            rim_point: rimPoint,
          }}
          history={history}
          aiReport={aiReport}
          userName={user?.name || user?.email}
          logoUrl={`${window.location.origin}/logo.png`}
          t={t}
          lang={(typeof localStorage !== 'undefined' && localStorage.getItem('pm_lang')) || 'es'}
        />
      )}

      {activeInboxAttempt && (
        <VoiceRecorderModal
          attemptId={activeInboxAttempt.id}
          playerName={activeInboxAttempt.player_name}
          onClose={() => setActiveInboxAttempt(null)}
          onSent={(comment) => {
            // Optimistically attach the comment to the inbox row so the
            // button flips from "Reply" to "Listen" without a refetch.
            setInbox((rows) => rows.map((r) => r.id === activeInboxAttempt.id ? { ...r, coach_comment: comment } : r));
          }}
        />
      )}
    </Layout>
  );
}
